import React, {useEffect, useState, useMemo} from 'react';
import api from '../api/axiosInstance';
import {Button} from "../components/Button";

const FIXED_SOURCES = ['queetstonepage', 'direct', 'metaAd'];
const RAW_LIMIT = 25;

export default function AddToCartDataPage() {
    const [report, setReport] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [days, setDays] = useState(7);

    const [sortBy, setSortBy] = useState('total.clicks');
    const [sortDir, setSortDir] = useState('desc');

    const [rawClicks, setRawClicks] = useState([]);
    const [rawPage, setRawPage] = useState(1);
    const [rawTotalPages, setRawTotalPages] = useState(1);
    const [confirmDeleteId, setConfirmDeleteId] = useState(null);

    useEffect(() => {
        const fetchReport = async () => {
            setLoading(true);
            try {
                const res = await api.get('/admin/reports/addToCartData', {
                    params: {days}
                });
                setReport(res.data.report);
            } catch (err) {
                console.error(err);
                setError('Failed to load Add to Cart report.');
            } finally {
                setLoading(false);
            }
        };

        fetchReport();
    }, [days]);

    /* =======================
       Fetch raw click data
       ======================= */
    useEffect(() => {
        const fetchRawClicks = async () => {
            const res = await api.get('/admin/reports/addToCartClicks', {
                params: {
                    page: rawPage,
                    limit: RAW_LIMIT
                }
            });

            setRawClicks(res.data.items);
            setRawTotalPages(res.data.totalPages);
        };

        fetchRawClicks();
    }, [rawPage]);

    /* =======================
       Sorting
       ======================= */
    const sortedReport = useMemo(() => {
        return [...report].sort((a, b) => {
            const getValue = (item, path) => {
                return path.split('.').reduce((val, key) => val?.[key], item) ?? 0;
            };

            const aVal = getValue(a, sortBy);
            const bVal = getValue(b, sortBy);

            if (typeof aVal === 'string') {
                return sortDir === 'asc'
                    ? aVal.localeCompare(bVal)
                    : bVal.localeCompare(aVal);
            }

            return sortDir === 'asc' ? aVal - bVal : bVal - aVal;
        });
    }, [report, sortBy, sortDir]);

    const totals = useMemo(() => {
        const result = {
            totalClicks: 0,
            totalQty: 0,
            bySource: {
                queetstonepage: 0,
                direct: 0,
                metaAd: 0
            }
        };

        for (const r of report) {
            result.totalClicks += r.total.clicks;
            result.totalQty += r.total.quantity;

            for (const source of FIXED_SOURCES) {
                result.bySource[source] += r.bySource?.[source]?.clicks ?? 0;
            }
        }

        return result;
    }, [report]);

    const handleSort = (key) => {
        if (sortBy === key) {
            setSortDir(sortDir === 'asc' ? 'desc' : 'asc');
        } else {
            setSortBy(key);
            setSortDir('desc');
        }
    };

    const renderSortArrow = (key) => {
        if (sortBy !== key) return null;
        return sortDir === 'asc' ? ' ▲' : ' ▼';
    };

    const handleDeleteClick = async (id) => {

        await api.delete(`/admin/reports/addToCartClicks/${id}`);
        setRawClicks(prev => prev.filter(c => c._id !== id));
    };

    if (loading) return <p className="text-center mt-10">Loading...</p>;
    if (error) return <p className="text-center mt-10 text-red-600">{error}</p>;

    return (
        <div className="max-w-6xl mx-auto px-4 py-10">
            <h1 className="text-3xl font-bold mb-6 text-center">
                Add to Cart Report (Last {days} Days)
            </h1>

            <div className="mb-4 flex items-center gap-2">
                <label htmlFor="days" className="font-semibold">Show last</label>
                <select
                    id="days"
                    value={days}
                    onChange={e => setDays(Number(e.target.value))}
                    className="border rounded px-2 py-1"
                >
                    <option value={7}>7 days</option>
                    <option value={14}>14 days</option>
                    <option value={30}>30 days</option>
                    <option value={60}>60 days</option>
                </select>
                <span>days</span>
            </div>

            <div className="overflow-x-auto">
                <table className="min-w-full bg-white border border-gray-200 text-sm">
                    <thead>
                    <tr className="bg-gray-100">
                        <th
                            className="py-2 px-4 border-b text-left cursor-pointer"
                            onClick={() => handleSort('title')}
                        >
                            Product{renderSortArrow('title')}
                        </th>
                        <th
                            className="py-2 px-4 border-b text-right cursor-pointer"
                            onClick={() => handleSort('total.clicks')}
                        >
                            Total Clicks{renderSortArrow('total.clicks')}
                        </th>
                        <th
                            className="py-2 px-4 border-b text-right cursor-pointer"
                            onClick={() => handleSort('total.quantity')}
                        >
                            Total Qty{renderSortArrow('total.quantity')}
                        </th>

                        {FIXED_SOURCES.map(source => (
                            <th
                                key={source}
                                className="py-2 px-4 border-b text-right cursor-pointer"
                                onClick={() => handleSort(`bySource.${source}.clicks`)}
                            >
                                {source} Clicks{renderSortArrow(`bySource.${source}.clicks`)}
                            </th>
                        ))}

                        <th
                            className="py-2 px-4 border-b text-left cursor-pointer"
                            onClick={() => handleSort('firstClickedAt')}
                        >
                            First Seen{renderSortArrow('firstClickedAt')}
                        </th>
                        <th
                            className="py-2 px-4 border-b text-left cursor-pointer"
                            onClick={() => handleSort('lastClickedAt')}
                        >
                            Last Seen{renderSortArrow('lastClickedAt')}
                        </th>
                    </tr>
                    </thead>

                    <tbody>
                    {sortedReport.map((r, i) => (
                        <tr key={i} className={i % 2 === 0 ? 'bg-gray-50' : ''}>
                            <td className="py-2 px-4 border-b">{r.title}</td>
                            <td className="py-2 px-4 border-b text-right">{r.total.clicks}</td>
                            <td className="py-2 px-4 border-b text-right">{r.total.quantity}</td>

                            {FIXED_SOURCES.map(source => (
                                <td key={source} className="py-2 px-4 border-b text-right">
                                    {r.bySource?.[source]?.clicks ?? 0}
                                </td>
                            ))}

                            <td className="py-2 px-4 border-b">
                                {new Date(r.firstClickedAt).toLocaleDateString()}
                            </td>
                            <td className="py-2 px-4 border-b">
                                {new Date(r.lastClickedAt).toLocaleDateString()}
                            </td>
                        </tr>
                    ))}

                    <tr className="bg-gray-200 font-semibold">
                        <td className="py-2 px-4 border-b">Totals</td>
                        <td className="py-2 px-4 border-b text-right">{totals.totalClicks}</td>
                        <td className="py-2 px-4 border-b text-right">{totals.totalQty}</td>
                        {FIXED_SOURCES.map(source => (
                            <td key={source} className="py-2 px-4 border-b text-right">
                                {totals.bySource[source]}
                            </td>
                        ))}
                        <td />
                        <td />
                    </tr>
                    </tbody>
                </table>
            </div>

            <h2 className="text-2xl font-bold mt-12 mb-4">
                Raw Add to Cart Clicks
            </h2>

            <div className="overflow-x-auto">
                <table className="min-w-full bg-white border border-gray-200 text-sm">
                    <thead>
                    <tr className="bg-gray-100">
                        <th className="py-2 px-4 border-b text-left">Product</th>
                        <th className="py-2 px-4 border-b text-left">Source</th>
                        <th className="py-2 px-4 border-b text-left">Clicked At</th>
                        <th className="py-2 px-4 border-b text-center">Actions</th>
                    </tr>
                    </thead>
                    <tbody>
                    {rawClicks.map(click => (
                        <tr key={click._id}>
                            <td className="py-2 px-4 border-b">{click.title}</td>
                            <td className="py-2 px-4 border-b">{click.source}</td>
                            <td className="py-2 px-4 border-b">
                                {new Date(click.createdAt).toLocaleString()}
                            </td>
                            <td className="py-2 px-4 border-b text-center">
                                {confirmDeleteId === click._id ? (
                                    <span className="flex items-center gap-2">
                                        <Button
                                            onClick={async () => {
                                                await handleDeleteClick(click._id);
                                                setConfirmDeleteId(null);
                                            }}
                                            className="px-2 py-1 bg-red-600 text-white rounded text-sm"
                                        >
                                            Confirm
                                        </Button>
                                        <Button
                                            onClick={() => setConfirmDeleteId(null)}
                                            className="px-2 py-1 border rounded text-sm"
                                        >
                                            Cancel
                                        </Button>
                                    </span>
                                ) : (
                                    <button
                                        onClick={() => setConfirmDeleteId(click._id)}
                                        className="text-red-600 hover:underline"
                                    >
                                        Delete
                                    </button>
                                )}

                            </td>
                        </tr>
                    ))}
                    </tbody>
                </table>
            </div>

            <div className="flex justify-between items-center mt-4">
                <button
                    disabled={rawPage === 1}
                    onClick={() => setRawPage(p => p - 1)}
                    className="px-3 py-1 border rounded disabled:opacity-50"
                >
                    Prev
                </button>

                <span>
                    Page {rawPage} of {rawTotalPages}
                </span>

                <button
                    disabled={rawPage === rawTotalPages}
                    onClick={() => setRawPage(p => p + 1)}
                    className="px-3 py-1 border rounded disabled:opacity-50"
                >
                    Next
                </button>
            </div>
        </div>
    );
}
